/**
 * The head pose the cardless measurement reads from MediaPipe, copied verbatim from the approved 4B-A.1 prototype (`spikes/pd-calibration/src/frame-analysis.ts`).
 *
 * Only what the cardless path calls lives here: the two pose functions, the landmark indices they read and the two shapes they speak in. The card method they sat beside in the spike is not part of the product. The arithmetic is unchanged and `test/equivalence.test.ts` pins it.
 */

/** MediaPipe Face Mesh indices read here. Which iris is which is recorded against the card, never assumed. */
export const LANDMARKS = {
  irisA: { centre: 468, ring: [469, 470, 471, 472] },
  irisB: { centre: 473, ring: [474, 475, 476, 477] },
  noseBridge: 168,
  eyeOuterA: 33,
  eyeInnerA: 133,
  eyeOuterB: 263,
  eyeInnerB: 362,
  forehead: 10,
  chin: 152,
} as const;

export interface Pose {
  yawDeg: number;
  pitchDeg: number;
  rollDeg: number;
  source: 'MATRIX' | 'LANDMARKS';
}

export interface FaceLandmarks {
  /** Normalised to the frame, as MediaPipe returns them. */
  points: readonly { x: number; y: number; z: number }[];
  blinkLeft?: number;
  blinkRight?: number;
  /** MediaPipe's 4×4 facial transformation matrix, flattened. */
  matrix?: readonly number[];
}

const deg = (r: number): number => (r * 180) / Math.PI;

/**
 * Head pose from MediaPipe's transformation matrix, in this spike's camera frame (x right, y down, z away).
 *
 * MediaPipe's metric space has y up and the camera looking down −z, so the rotation is conjugated by diag(1, −1, −1). The matrix layout is not documented, so it is read from where the rigid transform's 0 0 0 1 row sits. Whether MediaPipe's own sign conventions hold is a device-run question; the kit therefore records pose-free results alongside.
 */
export function poseFromMatrix(data: readonly number[]): Pose | null {
  if (data.length !== 16 || Math.abs(data[15]! - 1) > 1e-6) return null;
  const columnMajor = Math.abs(data[3]!) + Math.abs(data[7]!) + Math.abs(data[11]!) < 1e-6;
  const rowMajor = Math.abs(data[12]!) + Math.abs(data[13]!) + Math.abs(data[14]!) < 1e-6;
  if (columnMajor === rowMajor) return null;
  const at = (r: number, c: number): number => (columnMajor ? data[c * 4 + r]! : data[r * 4 + c]!);
  const flip = [1, -1, -1] as const;
  const scale = [0, 1, 2].map((c) => Math.hypot(at(0, c), at(1, c), at(2, c)));
  const R = (r: number, c: number): number => (flip[r]! * at(r, c) * flip[c]!) / scale[c]!;
  // R = Ry(yaw) · Rx(pitch) · Rz(roll), the decomposition the scene and estimator use.
  return {
    yawDeg: deg(Math.atan2(R(0, 2), R(2, 2))),
    pitchDeg: deg(Math.asin(Math.max(-1, Math.min(1, -R(1, 2))))),
    rollDeg: deg(Math.atan2(R(1, 0), R(1, 1))),
    source: 'MATRIX',
  };
}

/** A rougher pose from the landmarks' own depths, named by image side so a mirrored frame reads as its mirror. */
export function poseFromLandmarks(
  points: FaceLandmarks['points'],
  width: number,
  height: number,
): Pose | null {
  const p = (i: number) => points[i];
  const a = p(LANDMARKS.eyeOuterA);
  const b = p(LANDMARKS.eyeOuterB);
  const top = p(LANDMARKS.forehead);
  const bottom = p(LANDMARKS.chin);
  if (!a || !b || !top || !bottom) return null;
  const [left, right] = a.x < b.x ? [a, b] : [b, a];
  // MediaPipe z shrinks towards the camera and shares x's scale; positive yaw brings the image-right side nearer.
  const yaw = Math.atan2((left.z - right.z) * width, (right.x - left.x) * width);
  const pitch = Math.atan2((bottom.z - top.z) * width, (bottom.y - top.y) * height);
  const roll = Math.atan2((right.y - left.y) * height, (right.x - left.x) * width);
  return { yawDeg: deg(yaw), pitchDeg: deg(pitch), rollDeg: deg(roll), source: 'LANDMARKS' };
}
